import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroller';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Toolbar from '@mui/material/Toolbar';
import Grid from '@mui/material/Grid';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import ExploreIcon from '@mui/icons-material/Explore';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PlaceIcon from '@mui/icons-material/Place';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useMediaQuery } from 'react-responsive';
import agent from '../../agent';
import CommonLoader from '../Loaders/CommonLoader';
import SmallTopLoader from '../Loaders/SmallTopLoader';
import { DEFAULT_GRADIENT, DEFAULT_GRADIENT_HOVER, DEFAULT_THEME_COLOR } from '../../constants/theme';
import { formatDateAgo } from '../../utils/dateUtils';
import { getPreviewCarousel } from '../../utils/commonUtils';
import { sanitizeHtml } from '../../utils/sanitize';
import {
  COLOR_FINISHED, COLOR_IN_PROGRESS, COLOR_PLANNED, COLOR_CANCELED,
  STATUS_FINISHED, STATUS_IN_PROGRESS, STATUS_PLANNED, STATUS_CANCELED,
} from '../../constants/commonConstants';

const STATUS_COLORS = {
  [STATUS_IN_PROGRESS]: COLOR_IN_PROGRESS,
  [STATUS_FINISHED]: COLOR_FINISHED,
  [STATUS_PLANNED]: COLOR_PLANNED,
  [STATUS_CANCELED]: COLOR_CANCELED,
};

const FreakCard = ({ freak, onCTAClick }) => {
  const statusColor = STATUS_COLORS[freak.status] || COLOR_FINISHED;

  return (
    <Box
      sx={{
        background: 'linear-gradient(135deg, #0d1b2a 0%, #0d2a1e 100%)',
        border: '1px solid rgba(255,255,255,0.07)',
        borderRadius: 3,
        overflow: 'hidden',
        mb: 2,
        transition: 'box-shadow 0.18s',
        '&:hover': { boxShadow: '0 4px 24px rgba(54,201,162,0.15)', borderColor: 'rgba(54,201,162,0.25)' },
      }}
    >
      {/* Media carousel */}
      {getPreviewCarousel(freak) && (
        <Box sx={{ maxHeight: 240, overflow: 'hidden' }}>
          {getPreviewCarousel(freak)}
        </Box>
      )}

      <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
        {/* Author row */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar
              src={freak.author?.imageUrl}
              alt={freak.author?.username}
              sx={{ width: 32, height: 32, border: '2px solid rgba(54,201,162,0.35)' }}
            />
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 13, color: DEFAULT_THEME_COLOR, lineHeight: 1.1 }}>
                {freak.author?.username}
              </Typography>
              <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
                {formatDateAgo(freak.createdAt)}
              </Typography>
            </Box>
          </Box>
          <Chip
            label={freak.status}
            size="small"
            sx={{
              backgroundColor: `${statusColor}22`,
              color: statusColor,
              border: `1px solid ${statusColor}55`,
              fontWeight: 700,
              fontSize: 10,
              height: 20,
            }}
          />
        </Box>

        {/* Title */}
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, color: 'white', fontSize: { xs: '1rem', sm: '1.1rem' }, mb: 0.75, lineHeight: 1.3 }}
        >
          {freak.title}
        </Typography>

        {/* Meta: location + date */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
          {freak.map?.description && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PlaceIcon sx={{ fontSize: 13, color: 'rgba(255,255,255,0.4)' }} />
              <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>
                {freak.map.description}
              </Typography>
            </Box>
          )}
          {freak.startDate && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarTodayIcon sx={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }} />
              <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>
                {new Date(freak.startDate).toLocaleDateString()}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Body preview */}
        {freak.body && (
          <Box
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(freak.body) }}
            sx={{
              fontSize: 13,
              color: 'rgba(255,255,255,0.55)',
              mb: 1.5,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              '& p, & ul, & ol, & h1, & h2, & h3, & h4': { margin: 0, padding: 0 },
              '& a': { color: 'rgba(54,201,162,0.8)', textDecoration: 'none' },
            }}
          />
        )}

        {/* Tags */}
        {freak.tagList?.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
            {freak.tagList.map(tag => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                sx={{
                  background: 'linear-gradient(135deg, rgba(54,201,162,0.12) 0%, rgba(79,172,254,0.12) 100%)',
                  color: '#4facfe',
                  border: '1px solid rgba(79,172,254,0.25)',
                  fontWeight: 600,
                  fontSize: 10,
                  height: 20,
                }}
              />
            ))}
          </Box>
        )}

        {/* Footer: stats + CTA */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 1, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <FavoriteIcon sx={{ fontSize: 15, color: '#e05' }} />
              <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>{freak.favoritesCount || 0}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ChatBubbleOutlineIcon sx={{ fontSize: 15, color: 'rgba(255,255,255,0.4)' }} />
              <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>{freak.commentsCount || 0}</Typography>
            </Box>
          </Box>
          <Button
            size="small"
            variant="outlined"
            onClick={onCTAClick}
            sx={{
              borderColor: 'rgba(54,201,162,0.4)',
              color: DEFAULT_THEME_COLOR,
              fontWeight: 700,
              fontSize: 12,
              borderRadius: 2,
              px: 2,
              '&:hover': { borderColor: DEFAULT_THEME_COLOR, background: 'rgba(54,201,162,0.08)' },
              '&:focus': { outline: 'none' },
            }}
          >
            Join to view
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

const FreaksRestricted = ({ context }) => {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery({ minWidth: 992 });

  const [freaks, setFreaks] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadMore(0);
  }, []);

  const loadMore = async (nextPage) => {
    if (!hasMore && nextPage !== 0) return;
    try {
      const results = await agent.Freaks.restricted(nextPage, context || 'wild-off-road', 'mobile');
      const items = results?.freaks || results || [];
      setFreaks(prev => nextPage === 0 ? items : [...prev, ...items]);
      setPage(nextPage + 1);
      if (items.length === 0) setHasMore(false);
    } catch {
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(180deg, #0d1b2a 0%, #091420 100%)', pb: 6 }}>

      {/* Toolbar */}
      <Toolbar
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mt: 'env(safe-area-inset-top, 0px)',
          background: 'linear-gradient(135deg, #0d1b2a 0%, #0d3d2e 100%)',
          boxShadow: '0 2px 12px rgba(0,0,0,0.4)',
          px: { xs: 2, sm: 3 },
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography
            onClick={() => navigate('/about')}
            sx={{
              cursor: 'pointer',
              background: DEFAULT_GRADIENT,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontWeight: 800,
              fontSize: '1.2rem',
              letterSpacing: '-0.02em',
              whiteSpace: 'nowrap',
            }}
          >
            Freakland
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<LockOpenIcon />}
            onClick={() => navigate('/register')}
            sx={{
              background: DEFAULT_GRADIENT,
              color: 'white',
              fontWeight: 700,
              borderRadius: 2,
              fontSize: 12,
              border: 'none',
              boxShadow: '0 2px 8px rgba(54,201,162,0.35)',
              whiteSpace: 'nowrap',
              '&:hover': { background: DEFAULT_GRADIENT_HOVER },
              '&:focus': { outline: 'none' },
            }}
          >
            {isDesktop ? 'Join Free' : 'Join'}
          </Button>
        </Box>
        <Button
          size="small"
          variant="text"
          onClick={() => navigate('/login')}
          sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, '&:hover': { color: 'white' }, '&:focus': { outline: 'none' } }}
        >
          Log in
        </Button>
      </Toolbar>

      {/* Hero */}
      <Box
        sx={{
          px: { xs: 2, sm: 4 },
          pt: { xs: 3, sm: 4 },
          pb: 3,
          textAlign: 'center',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <ExploreIcon sx={{ fontSize: 40, color: DEFAULT_THEME_COLOR, mb: 1 }} />
        <Typography
          variant="h5"
          sx={{
            fontWeight: 800,
            background: DEFAULT_GRADIENT,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            mb: 0.75,
          }}
        >
          Public Freaks
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 14 }}>
          Browsing in preview mode.{' '}
          <Box
            component="span"
            onClick={() => navigate('/register')}
            sx={{ color: DEFAULT_THEME_COLOR, cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
          >
            Join free
          </Box>
          {' '}to participate, comment, and create.
        </Typography>
      </Box>

      {/* List */}
      <Box sx={{ px: { xs: 1.5, sm: 3, md: 6 }, pt: 2, maxWidth: 720, mx: 'auto' }}>
        {isLoading ? (
          <SmallTopLoader />
        ) : freaks.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.4)', mb: 3 }}>No public freaks available right now.</Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/register')}
              sx={{ background: DEFAULT_GRADIENT, color: 'white', fontWeight: 700, borderRadius: 2, '&:hover': { background: DEFAULT_GRADIENT_HOVER } }}
            >
              Create an account to see more
            </Button>
          </Box>
        ) : (
          <InfiniteScroll
            pageStart={1}
            loadMore={() => loadMore(page)}
            hasMore={hasMore}
            loader={<CommonLoader key="loader" />}
          >
            <Grid container spacing={0}>
              {freaks.map((freak, idx) => (
                <Grid item xs={12} key={freak.slug || idx}>
                  <FreakCard freak={freak} onCTAClick={() => navigate('/register')} />
                </Grid>
              ))}
            </Grid>
          </InfiniteScroll>
        )}
      </Box>

      {/* Sticky CTA banner */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          background: 'linear-gradient(180deg, rgba(13,27,42,0) 0%, rgba(13,27,42,0.97) 40%)',
          pt: 4,
          pb: 'max(16px, env(safe-area-inset-bottom, 16px))',
          px: 2,
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate('/register')}
          sx={{
            background: DEFAULT_GRADIENT,
            color: 'white',
            fontWeight: 700,
            borderRadius: 3,
            px: 5,
            boxShadow: '0 4px 24px rgba(54,201,162,0.4)',
            '&:hover': { background: DEFAULT_GRADIENT_HOVER },
            '&:focus': { outline: 'none' },
            pointerEvents: 'auto',
          }}
        >
          Join Free to Participate
        </Button>
      </Box>
    </Box>
  );
};

export default FreaksRestricted;
